import { useEffect, useRef, useState } from "react";
import type { PortalContactSuggestions } from "../data/customer-portal-access-types";

const SUGGEST_DEBOUNCE_MS = 200;

const NO_SUGGESTIONS: PortalContactSuggestions = {
  type: "portal_contact_suggestions",
  people: [],
};

/**
 * The contact picker's lookup, headless so every rendering of the picker (the
 * staff app's popover, the staff view's inline list) debounces and discards
 * stale answers the same way. Looks up only while `active`; with no `suggest`
 * it never looks and offers nothing beyond the host's own people.
 */
export function usePortalContactSuggestions(
  suggest: ((query: string) => Promise<PortalContactSuggestions>) | null,
  query: string,
  active: boolean,
): {
  suggestions: PortalContactSuggestions;
  loading: boolean;
  lookupFailed: boolean;
} {
  const [suggestions, setSuggestions] =
    useState<PortalContactSuggestions>(NO_SUGGESTIONS);
  const [loading, setLoading] = useState(false);
  const [lookupFailed, setLookupFailed] = useState(false);
  const latest = useRef(0);
  // Hosts pass an inline arrow, a new function every render. Held in a ref so
  // that a re-render does not look up again; only the query and `active` do.
  const suggestRef = useRef(suggest);
  suggestRef.current = suggest;
  const canSuggest = suggest !== null;

  useEffect(() => {
    if (!active || !canSuggest) return;
    const handle = setTimeout(() => {
      const mine = ++latest.current;
      setLoading(true);
      setLookupFailed(false);
      suggestRef.current!(query.trim())
        .then((result) => {
          if (mine === latest.current) setSuggestions(result);
        })
        .catch(() => {
          if (mine === latest.current) setLookupFailed(true);
        })
        .finally(() => {
          if (mine === latest.current) setLoading(false);
        });
    }, SUGGEST_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [active, query, canSuggest]);

  return { suggestions, loading, lookupFailed };
}
